import { Injectable, inject } from "@angular/core";
import { Observable, forkJoin, from } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { BlobDownloadResponseParsed, BlockBlobClient } from "@azure/storage-blob";

import { DocumentsService } from "./data/documents.service";
import { DocumentUploadItem } from "../types/documentuploaditem";
import { DocumentUploadModel } from "../types/documentuploadmodel";

@Injectable({
    "providedIn": "root"
})
export class AzureBlobService {
    private documentsService = inject(DocumentsService);

    uploadBlob(items: DocumentUploadItem[]): Observable<void> {
        const uploadInfos = items.map(item => item.uploadInfo);

        return this.documentsService.uploadDocument(uploadInfos).pipe(
            switchMap((uploadModels: DocumentUploadModel[]) =>
                this.uploadFilesToBlob(items, uploadModels)
            ),
            switchMap((uploadedModels: DocumentUploadModel[]) => {
                const blobStagingIds = uploadedModels.map(model => model.blobStagingId);
                return this.documentsService.uploadComplete(blobStagingIds).pipe(map(() => undefined));
            })
        );
    }

    getBlob(sasUri: string): Observable<Blob> {
        const blobClient = new BlockBlobClient(sasUri);

        const download$ = from(blobClient.download(0));
        return download$.pipe(
            switchMap((downloadResponse: BlobDownloadResponseParsed) => {
                return from(downloadResponse.blobBody);
            })
        );
    }

    private uploadFileToBlob(item: DocumentUploadItem, uploadModel: DocumentUploadModel): Observable<DocumentUploadModel> {
        const blockBlobClient = new BlockBlobClient(uploadModel.documentUploadUrl);

        return from(
            item.file.arrayBuffer().then(buffer =>
                blockBlobClient.uploadData(buffer, {
                    blobHTTPHeaders: {
                        blobContentType: item.file.type
                    }
                })
            ).then(() => uploadModel)
        );
    }

    //Staging returns one upload target per requested file, in request order, so files are paired by index.
    //Pairing by file name instead would collapse same-named files (one document attached to both a PO and one of its
    //line items) onto a single target: they would overwrite one blob and leave the other staging rows orphaned.
    private uploadFilesToBlob(items: DocumentUploadItem[], uploadModels: DocumentUploadModel[]): Observable<DocumentUploadModel[]> {
        if (uploadModels.length !== items.length) {
            throw new Error(`Upload staging mismatch: requested ${items.length} file(s) but received ${uploadModels.length} upload target(s).`);
        }

        const uploadObservables = items.map((item, index) => this.uploadFileToBlob(item, uploadModels[index]));

        return forkJoin(uploadObservables);
    }
}
